/** "Feb 2026"-style month label; UTC so the first-of-month date can't shift. */
export function formatPeriodMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

export function formatPeriodSpan(row: {
  periodStart: string;
  lastPeriodStart?: string | null;
}): string {
  const first = formatPeriodMonth(row.periodStart);
  const last = row.lastPeriodStart ? formatPeriodMonth(row.lastPeriodStart) : first;
  return first === last ? first : `${first} – ${last}`;
}

function monthIndex(iso: string): number {
  const d = new Date(iso);
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
}

/** Calendar months from periodStart through lastPeriodStart, inclusive. */
export function monthsInSpan(row: {
  periodStart: string;
  lastPeriodStart?: string | null;
}): number {
  if (!row.lastPeriodStart) return 1;
  return monthIndex(row.lastPeriodStart) - monthIndex(row.periodStart) + 1;
}

function formatMonthOnly(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
}

/** Consecutive months collapsed into labeled ranges, oldest first. */
export function formatMismatchMonthRanges(isoMonths: readonly string[]): string[] {
  const sorted = [...isoMonths].sort((a, b) => monthIndex(a) - monthIndex(b));
  const ranges: { start: string; end: string }[] = [];
  for (const iso of sorted) {
    const last = ranges[ranges.length - 1];
    if (last && monthIndex(iso) === monthIndex(last.end) + 1) last.end = iso;
    else ranges.push({ start: iso, end: iso });
  }
  return ranges.map(({ start, end }) => {
    if (start === end) return formatPeriodMonth(start);
    const sameYear = new Date(start).getUTCFullYear() === new Date(end).getUTCFullYear();
    if (sameYear) return `${formatMonthOnly(start)}–${formatPeriodMonth(end)}`;
    return `${formatPeriodMonth(start)} – ${formatPeriodMonth(end)}`;
  });
}

export function formatMismatchMonths(isoMonths: readonly string[]): string {
  return formatMismatchMonthRanges(isoMonths).join(" · ");
}
